/**
 * Shared contract for a sales tool's optional file (Almikia Epic EH). Both the
 * Supabase and FakeRest providers use the same private bucket id, the same
 * type/size limits and the same short signed URL expiry.
 */

import type { ResourceCallbacks } from "ra-core";

/** Message shown when a delete is attempted: tools are archived instead. */
export const SALES_TOOL_DELETE_DENIED_MESSAGE =
  "Las herramientas no se eliminan, se archivan";

/**
 * Lifecycle callbacks shared by both providers: `sales_tools` is archived
 * (`archived_at`), never deleted, mirroring the database delete trigger.
 */
export const salesToolsDeleteCallbacks: ResourceCallbacks = {
  resource: "sales_tools",
  beforeDelete: async () => {
    throw new Error(SALES_TOOL_DELETE_DENIED_MESSAGE);
  },
  beforeDeleteMany: async () => {
    throw new Error(SALES_TOOL_DELETE_DENIED_MESSAGE);
  },
};

/** Private storage bucket id (see supabase/schemas/07_storage.sql). */
export const SALES_TOOLS_BUCKET = "sales-tools";

/** Maximum accepted tool file size, in bytes (10 MiB). */
export const SALES_TOOL_FILE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** Signed URL expiry for a tool file download, in seconds. */
export const SALES_TOOL_SIGNED_URL_EXPIRY_SECONDS = 60;

/** Allowed file MIME types mapped to the extension used in storage paths. */
const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
};

export const SALES_TOOL_ALLOWED_MIME_TYPES = Object.keys(
  EXTENSION_BY_MIME_TYPE,
);

/** Reason code for a rejected upload; the UI owns the Spanish label. */
export type SalesToolFileRejectionReason = "invalid_type" | "too_large";

export type SalesToolFileValidationResult =
  | { valid: true }
  | { valid: false; reason: SalesToolFileRejectionReason };

/**
 * Validates a candidate tool file before any network/storage call: PDF, PNG
 * or JPEG, no larger than {@link SALES_TOOL_FILE_MAX_SIZE_BYTES}.
 */
export function validateSalesToolFile(
  file: Pick<File, "type" | "size">,
): SalesToolFileValidationResult {
  if (!SALES_TOOL_ALLOWED_MIME_TYPES.includes(file.type)) {
    return { valid: false, reason: "invalid_type" };
  }
  if (file.size > SALES_TOOL_FILE_MAX_SIZE_BYTES) {
    return { valid: false, reason: "too_large" };
  }
  return { valid: true };
}

/**
 * Builds a fresh, unique storage path for a tool file upload. The path never
 * carries the original file name, so every upload is a new object and an
 * existing one is never overwritten.
 */
export function buildSalesToolStoragePath(mimeType: string): string {
  return `${crypto.randomUUID()}.${EXTENSION_BY_MIME_TYPE[mimeType] ?? "bin"}`;
}
